/**
 * Main layout component — dark sidebar, light header, warm content area.
 * Design system: luxury-utilitarian (Bloomberg meets Aesop).
 */
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as LayoutDashboard,
  TrendingUp,
  ShoppingCart,
  Inventory as Package,
  BarChart as BarChart3,
  Settings,
  Logout as LogOut,
  ChevronRight,
  ExpandMore as ChevronDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';
import NotificationBell from '../Notifications/NotificationBell';
import CurrencySelector from '../Common/CurrencySelector';
import LanguageSwitcher from '../Common/LanguageSwitcher';


const drawerWidth = 240;

interface CurrentUser {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface MenuChild {
  text: string;
  path: string;
}

interface MenuGroup {
  label: string;
  items: MenuChild[];
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  key: string;
  path?: string;
  children?: MenuChild[];
  groups?: MenuGroup[];
}

// ─── Breadcrumb helper ───
const pathToLabel: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/forecasting': 'Forecasting',
  '/forecasting/sales-forecast': 'Sales Forecast',
  '/forecasting/sku-projection': 'SKU Projection',
  '/forecasting/manual-override': 'Manual Override',
  '/forecasting/cannibalization': 'Cannibalization',
  '/forecasting/year-over-year': 'Year-over-Year',
  '/forecasting/seasonal-profiles': 'Seasonal Profiles',
  '/forecasting-calendar': 'Calendar',
  '/replenishment': 'Replenishment',
  '/replenishment/projection': 'Replenishment Projection',
  '/replenishment/stock-transfer': 'Stock Transfer',
  '/replenishment/transfer-history': 'Transfer History',
  '/replenishment/order-validation': 'Order Validation',
  '/replenishment/order-history': 'Order History',
  '/inventory': 'Inventory Level',
  '/inventory/stockout-management': 'Stockout Management',
  '/inventory/stock-status': 'Stock Status',
  '/inventory/batch-tracking': 'Expiry & Batch Tracking',
  '/report-analytics/kpi/forecast-accuracy': 'Forecast Accuracy',
  '/report-analytics/kpi/service-level': 'Service Level',
  '/report-analytics/business-analysis/store-performance': 'Store Performance',
  '/report-analytics/business-analysis/category-performance': 'Category Performance',
  '/report-analytics/business-analysis/abc-classification': 'ABC Classification',
  '/report-analytics/business-analysis/ai-insights': 'AI Insights',
  '/user-management': 'User Management',
  '/settings': 'Parameters',
  '/sage-integration': 'ERP Integration',
  '/alerts': 'Alerts',
};

const parentLabels: Record<string, string> = {
  forecasting: 'Forecasting',
  inventory: 'Inventory',
  'report-analytics': 'Report Analytics',
  settings: 'Settings',
};

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const isMobile = useMediaQuery('(max-width:767px)');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.getCurrentUser();
        setCurrentUser(response.data);
      } catch {
        // Handled by centralized error emitter
      }
    };
    fetchCurrentUser();
  }, []);

  // Helper: get all navigable child paths from a menu item
  const getAllChildPaths = (item: MenuItem): string[] => {
    const paths: string[] = [];
    if (item.children) paths.push(...item.children.map((c) => c.path));
    if (item.groups) item.groups.forEach((g) => paths.push(...g.items.map((c) => c.path)));
    return paths;
  };

  // Auto-expand parent menu if a child route is active
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenMenus: Record<string, boolean> = {};
    menuItems.forEach((item) => {
      if (item.children || item.groups) {
        const isChildActive = getAllChildPaths(item).some((p) => currentPath === p);
        if (isChildActive) {
          newOpenMenus[item.key] = true;
        }
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    api.logout();
    navigate('/login');
  };

  const handleMenuToggle = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const menuItems: MenuItem[] = [
    {
      text: t('navigation.dashboard'),
      icon: <LayoutDashboard sx={{ fontSize: 18 }} />,
      path: '/dashboard',
      key: 'dashboard',
    },
    {
      text: t('navigation.forecasting'),
      icon: <TrendingUp sx={{ fontSize: 18 }} />,
      path: '/forecasting',
      key: 'forecasting',
    },
    {
      text: t('navigation.replenishment'),
      icon: <ShoppingCart sx={{ fontSize: 18 }} />,
      path: '/replenishment',
      key: 'replenishment',
    },
    {
      text: t('navigation.inventory'),
      icon: <Package sx={{ fontSize: 18 }} />,
      path: '/inventory',
      key: 'inventory',
    },
    {
      text: t('navigation.reportAnalytics', 'Report Analytics'),
      icon: <BarChart3 sx={{ fontSize: 18 }} />,
      path: '/report-analytics/kpi/forecast-accuracy',
      key: 'report-analytics',
    },
    {
      text: t('navigation.settings'),
      icon: <Settings sx={{ fontSize: 18 }} />,
      key: 'settings',
      children: [
        { text: t('navigation.userManagement'), path: '/user-management' },
        { text: t('navigation.parameters'), path: '/settings' },
        { text: t('navigation.sageIntegration', 'ERP Integration'), path: '/sage-integration' },
      ],
    },
  ];

  const isPathActive = (path: string) => location.pathname === path;

  const isParentActive = (item: MenuItem) => {
    if (item.path) {
      // For hub items, match by key prefix (e.g. 'report-analytics' matches /report-analytics/*)
      const keyPrefix = '/' + item.key;
      if (location.pathname.startsWith(keyPrefix)) return true;
      return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    }
    return getAllChildPaths(item).some((p) => isPathActive(p));
  };

  // ─── Build breadcrumb ───
  const getBreadcrumb = () => {
    const path = location.pathname;
    const pageLabel = pathToLabel[path] || path.split('/').pop() || '';
    // Find parent
    for (const item of menuItems) {
      if (getAllChildPaths(item).some((p) => p === path)) {
        return { parent: parentLabels[item.key] || item.text, current: pageLabel };
      }
    }
    return { parent: null, current: pageLabel };
  };

  const breadcrumb = getBreadcrumb();

  // ─── Sidebar content ───
  const sidebarContent = (
    <Box
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'var(--color-sidebar-bg)',
        color: 'var(--color-sidebar-text)',
        overflow: 'hidden',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          paddingInlineStart: '20px',
          paddingInlineEnd: '16px',
          flexShrink: 0,
        }}
      >
        <img
          src="/IRIS-LOGO.png"
          alt="IRIS Logo"
          style={{ width: 36, height: 'auto' }}
        />
        <Box sx={{ marginInlineStart: 1.5 }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#FFFFFF',
              letterSpacing: '0.05em',
              lineHeight: 1.2,
              fontFamily: 'var(--font-heading)',
            }}
          >
            IRIS
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              color: 'var(--color-sidebar-text)',
              lineHeight: 1,
              fontFamily: 'var(--font-body)',
            }}
          >
            Luxury Distribution
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ height: '1px', bgcolor: 'var(--color-sidebar-divider)', mx: '12px' }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1, '&::-webkit-scrollbar': { width: 0 } }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.key}>
            {/* Parent menu item */}
            <Box
              onClick={() => {
                if (item.children || item.groups) {
                  handleMenuToggle(item.key);
                } else if (item.path) {
                  navigate(item.path);
                  if (mobileOpen) setMobileOpen(false);
                }
              }}
              sx={{
                height: 40,
                display: 'flex',
                alignItems: 'center',
                paddingInlineEnd: '16px',
                paddingInlineStart: '20px',
                cursor: 'pointer',
                position: 'relative',
                color: isParentActive(item) ? 'var(--color-sidebar-text-active)' : 'var(--color-sidebar-text)',
                bgcolor: isParentActive(item) ? 'var(--color-sidebar-bg-active)' : 'transparent',
                transition: 'background-color var(--transition-fast), color var(--transition-fast)',
                '&:hover': {
                  bgcolor: isParentActive(item) ? 'var(--color-sidebar-bg-active)' : 'var(--color-sidebar-bg-hover)',
                  color: '#FFFFFF',
                },
                // Gold border on inline-start edge for active item
                '&::before': isParentActive(item) ? {
                  content: '""',
                  position: 'absolute',
                  insetInlineStart: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  bgcolor: 'var(--color-sidebar-accent)',
                  borderRadius: isRtl ? '2px 0 0 2px' : '0 2px 2px 0',
                } : {},
              }}
            >
              {/* Icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', marginInlineEnd: 1.5 }}>
                {item.icon}
              </Box>
              {/* Text */}
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  flex: 1,
                  fontFamily: 'var(--font-body)',
                  lineHeight: 1,
                  color: 'inherit',
                }}
              >
                {item.text}
              </Typography>
              {/* Chevron for expandable */}
              {(item.children || item.groups) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'transform var(--transition-base)',
                    transform: openMenus[item.key] ? 'rotate(0deg)' : 'rotate(0deg)',
                  }}
                >
                  {openMenus[item.key] ? (
                    <ChevronDown sx={{ fontSize: 14 }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: 14 }} />
                  )}
                </Box>
              )}
            </Box>

            {/* Sub-menu items */}
            {item.children && (
              <Collapse in={openMenus[item.key]} timeout={200}>
                {item.children.map((child) => {
                  const childActive = isPathActive(child.path);
                  return (
                    <Box
                      key={child.path}
                      onClick={() => {
                        navigate(child.path);
                        if (mobileOpen) setMobileOpen(false);
                      }}
                      sx={{
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        paddingInlineStart: '40px',
                        paddingInlineEnd: '16px',
                        cursor: 'pointer',
                        color: childActive ? '#FFFFFF' : 'var(--color-sidebar-text)',
                        transition: 'color var(--transition-fast), background-color var(--transition-fast)',
                        '&:hover': {
                          bgcolor: 'var(--color-sidebar-bg-hover)',
                          color: '#FFFFFF',
                        },
                      }}
                    >
                      {/* Dot indicator */}
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          bgcolor: childActive ? 'var(--color-sidebar-accent)' : 'var(--color-sidebar-text)',
                          marginInlineEnd: 1.5,
                          flexShrink: 0,
                          opacity: childActive ? 1 : 0.8,
                          transition: 'all var(--transition-fast)',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: childActive ? 500 : 400,
                          fontFamily: 'var(--font-body)',
                          lineHeight: 1,
                          color: 'inherit',
                        }}
                      >
                        {child.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Collapse>
            )}

            {/* Grouped sub-menu items (with section headers) */}
            {item.groups && (
              <Collapse in={openMenus[item.key]} timeout={200}>
                {item.groups.map((group) => (
                  <React.Fragment key={group.label}>
                    {/* Group header */}
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--color-sidebar-text)',
                        opacity: 0.7,
                        paddingInlineStart: '44px',
                        pt: 1.2,
                        pb: 0.3,
                        fontFamily: 'var(--font-body)',
                        lineHeight: 1,
                      }}
                    >
                      {group.label}
                    </Typography>
                    {group.items.map((child) => {
                      const childActive = isPathActive(child.path);
                      return (
                        <Box
                          key={child.path}
                          onClick={() => {
                            navigate(child.path);
                            if (mobileOpen) setMobileOpen(false);
                          }}
                          sx={{
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            paddingInlineStart: '40px',
                            paddingInlineEnd: '16px',
                            cursor: 'pointer',
                            color: childActive ? '#FFFFFF' : 'var(--color-sidebar-text)',
                            transition: 'color var(--transition-fast), background-color var(--transition-fast)',
                            '&:hover': {
                              bgcolor: 'var(--color-sidebar-bg-hover)',
                              color: '#FFFFFF',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: childActive ? 'var(--color-sidebar-accent)' : 'var(--color-sidebar-text)',
                              marginInlineEnd: 1.5,
                              flexShrink: 0,
                              opacity: childActive ? 1 : 0.8,
                              transition: 'all var(--transition-fast)',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: childActive ? 500 : 400,
                              fontFamily: 'var(--font-body)',
                              lineHeight: 1,
                              color: 'inherit',
                            }}
                          >
                            {child.text}
                          </Typography>
                        </Box>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Bottom divider */}
      <Box sx={{ height: '1px', bgcolor: 'var(--color-sidebar-divider)', mx: '12px' }} />

      {/* User section at bottom */}
      <Box
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          px: '20px',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {currentUser ? (
          <>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'var(--color-sidebar-bg-active)',
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
              }}
            >
              {getInitials(currentUser.full_name || currentUser.email)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#FFFFFF',
                  fontWeight: 500,
                  fontFamily: 'var(--font-body)',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentUser.full_name || currentUser.email}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: 'var(--color-sidebar-text)',
                  fontFamily: 'var(--font-body)',
                  lineHeight: 1,
                }}
              >
                {currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1)}
              </Typography>
            </Box>
            <Tooltip title={t('navigation.logout')}>
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: 'var(--color-sidebar-text)',
                  '&:hover': { color: '#FFFFFF', bgcolor: 'var(--color-sidebar-bg-hover)' },
                }}
              >
                <LogOut sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Typography sx={{ fontSize: '12px', color: 'var(--color-sidebar-text)' }}>
            Loading...
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box dir="ltr" sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'var(--color-bg-primary)', direction: 'ltr' }}>
      {/* ─── Sidebar ─── */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile/tablet: temporary overlay */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
        {/* Desktop: permanent */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: 'none',
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>
      </Box>

      {/* ─── Main content area ─── */}
      <Box
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100vh',
          minHeight: 0,
          overflow: 'hidden',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        {/* ─── Top header bar ─── */}
        <Box
          sx={{
            height: 56,
            bgcolor: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border-subtle)',
            boxShadow: 'var(--shadow-sm)',
            display: 'flex',
            alignItems: 'center',
            px: 3,
            flexShrink: 0,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          {/* Mobile menu button */}
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              display: { md: 'none' },
              mr: 1,
              color: 'var(--color-text-secondary)',
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Breadcrumb */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {breadcrumb.parent && (
              <>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {breadcrumb.parent}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                    mx: 1,
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  /
                </Typography>
              </>
            )}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-body)',
              }}
            >
              {breadcrumb.current}
            </Typography>
          </Box>

          {/* Right side: notifications, language, avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationBell />
            <CurrencySelector />
            <LanguageSwitcher />
            {currentUser && (
              <Tooltip title={currentUser.full_name || currentUser.email}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-primary)',
                    fontSize: '11px',
                    fontWeight: 600,
                    fontFamily: 'var(--font-body)',
                    cursor: 'pointer',
                  }}
                >
                  {getInitials(currentUser.full_name || currentUser.email)}
                </Avatar>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Global filter bar removed */}

        {/* ─── Page content ─── */}
        <Box
          component="main"
          className="page-enter"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 'var(--page-padding)',
            maxWidth: 'var(--content-max-width)',
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
